// @file For frame backgrounds on tldraw canvas

import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { sanitizeUrl } from '@@/bits/url'
import { vSet } from '@@/bits/vue'
import {
  BackgroundType,
  BACKGROUND_TYPE_TO_URL_MAPPING,
  changeFrameBackground,
  generateCustomBackground,
  migrateDeprecatedBackgrounds,
  setUseThemeFrameBackground,
} from '@@/bits/whiteboard'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFramePreviewStore } from '@@/pinia/whiteboard_frame_preview'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { WhiteboardCardBackgroundsApi } from '@@/surface/api/whiteboard_card_backgrounds'
import type { WhiteboardCardBackground, WhiteboardDefaultFrameBackground } from '@@/types'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { cloneDeep, groupBy, isEqual } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { Editor, TLPage, TLPageId } from 'tldraw'
import { computed, ref } from 'vue'

export const useWhiteboardFrameBackgroundStore = defineStore('whiteboardFrameBackground', () => {
  const whiteboardFramePreviewStore = useWhiteboardFramePreviewStore()
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardStore = useWhiteboardStore()

  const frameBackgrounds = ref<WhiteboardCardBackground[]>([])
  const isPreviewingFrameBackgrounds = ref(false)
  const originalFrameBackgrounds = ref<Record<TLPageId, WhiteboardCardBackground>>({})
  const previewFrameBackgrounds = ref<Record<TLPageId, WhiteboardCardBackground>>({})

  const backgroundTypeToBackground = computed(() => {
    return frameBackgrounds.value.reduce<Record<string, WhiteboardCardBackground>>((acc, background) => {
      acc[background.id] = background
      return acc
    }, {})
  })
  const backgroundCategoryToBackgrounds = computed(() => {
    return groupBy(frameBackgrounds.value, (background) => background.category)
  })
  const displayedFrameBackgrounds = computed(() => {
    return isPreviewingFrameBackgrounds.value ? previewFrameBackgrounds.value : originalFrameBackgrounds.value
  })
  const isFrameBackgroundsPreviewDirty = computed(() => {
    return isPreviewingFrameBackgrounds.value && !isEqual(previewFrameBackgrounds.value, originalFrameBackgrounds.value)
  })

  async function fetchFrameBackgrounds(): Promise<void> {
    try {
      const backgrounds = await WhiteboardCardBackgroundsApi.fetch()
      frameBackgrounds.value = backgrounds
    } catch (error) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(error, { context: 'WhiteboardFrameBackgroundStoreFetchFrameBackgrounds' })
    }
  }

  function getPageBackgroundUrl(page: TLPage): string {
    const meta = page.meta
    const backgroundType = meta.backgroundType as string

    // First check if this is a standard background type with a predefined URL
    if (backgroundTypeToBackground.value[backgroundType] != null) {
      return backgroundTypeToBackground.value[backgroundType].url
    }

    // If no standard background found, fall back to custom background URL if it exists.
    // We store custom URLs as a fallback in case a background gets removed from Airtable.
    // This handles both user-uploaded backgrounds and removed Airtable backgrounds.
    return (
      sanitizeUrl(meta.customBackgroundUrl as string) ??
      backgroundTypeToBackground.value[BackgroundType.Plain]?.url ??
      defaultFrameBackground.value.backgroundUrl
    )
  }

  function generateOriginalFrameBackgrounds(): void {
    const tldrawEditor = getEditorObject()
    if (tldrawEditor == null) return
    const pages = tldrawEditor.getPages()
    for (const page of pages) {
      const meta = page.meta
      const customBackgroundUrl = sanitizeUrl(meta.customBackgroundUrl as string)
      const backgroundType = meta.backgroundType as string
      const background = backgroundTypeToBackground.value[backgroundType]

      // Handle background missing from Airtable
      if ((background == null || backgroundType === BackgroundType.Custom) && customBackgroundUrl != null) {
        vSet(originalFrameBackgrounds.value, page.id, generateCustomBackground(customBackgroundUrl))
      } else if (background != null) {
        vSet(originalFrameBackgrounds.value, page.id, background)
      } else {
        // The first page doesn't have background type after the sandbox is created
        // Show the Plain background
        vSet(originalFrameBackgrounds.value, page.id, backgroundTypeToBackground.value[BackgroundType.Plain])
      }
    }
  }

  function syncOriginalFrameBackgroundsWithFrameMeta(): void {
    generateOriginalFrameBackgrounds()
  }

  function startPreviewFrameBackgrounds(): void {
    isPreviewingFrameBackgrounds.value = true
    generateOriginalFrameBackgrounds()
    previewFrameBackgrounds.value = cloneDeep(originalFrameBackgrounds.value)
  }

  function stopPreviewFrameBackgrounds(): void {
    isPreviewingFrameBackgrounds.value = false
    previewFrameBackgrounds.value = {}
    // Revert to original frame backgrounds
    void whiteboardFramePreviewStore.updatePreviewForAllFrames()
  }

  function setPreviewFrameBackground({
    pageId,
    payload,
  }: {
    pageId: TLPageId
    payload: WhiteboardCardBackground
  }): void {
    previewFrameBackgrounds.value = {
      ...previewFrameBackgrounds.value,
      [pageId]: payload,
    }
  }

  // #region Default card background

  const defaultFrameBackground = ref<WhiteboardDefaultFrameBackground>({
    backgroundType: BackgroundType.Plain,
    backgroundUrl: BACKGROUND_TYPE_TO_URL_MAPPING[BackgroundType.Plain],
  })
  const previewDefaultFrameBackground = ref<WhiteboardDefaultFrameBackground>(defaultFrameBackground.value)
  const isPreviewingDefaultFrameBackground = ref(false)
  const isDefaultBackgroundConfigured = ref(false)

  const defaultFrameBackgroundName = computed(() => {
    return backgroundTypeToBackground.value[defaultFrameBackground.value.backgroundType]?.name ?? __('Custom')
  })
  const isDefaultFrameBackgroundPreviewDirty = computed(() => {
    return (
      isPreviewingDefaultFrameBackground.value &&
      !isEqual(previewDefaultFrameBackground.value, defaultFrameBackground.value)
    )
  })
  const displayedDefaultFrameBackground = computed(() => {
    return isPreviewingDefaultFrameBackground.value ? previewDefaultFrameBackground.value : defaultFrameBackground.value
  })

  function initializeDefaultFrameBackground({
    backgroundType,
    backgroundUrl,
    shouldCorrectUrlIfMismatch = false,
  }: {
    backgroundType: string
    backgroundUrl: string
    shouldCorrectUrlIfMismatch?: boolean
  }): void {
    if (backgroundType == null || backgroundUrl == null) {
      defaultFrameBackground.value = {
        backgroundType: BackgroundType.Plain,
        backgroundUrl: BACKGROUND_TYPE_TO_URL_MAPPING[BackgroundType.Plain],
      }
      previewDefaultFrameBackground.value = defaultFrameBackground.value
      isDefaultBackgroundConfigured.value = false
      return
    }

    const mappedBackground = backgroundTypeToBackground.value[backgroundType]
    const isMappedBackgroundMissing = mappedBackground == null
    // Compare background URLs between the one stored in the database and the current version from Airtable to detect outdated URLs
    const hasUrlMismatch = mappedBackground != null && backgroundUrl !== mappedBackground.url

    defaultFrameBackground.value = {
      backgroundType: isMappedBackgroundMissing ? BackgroundType.Custom : backgroundType,
      backgroundUrl: hasUrlMismatch ? mappedBackground.url : backgroundUrl,
    }
    previewDefaultFrameBackground.value = defaultFrameBackground.value
    isDefaultBackgroundConfigured.value = true

    // Update if URL needs correction
    if (whiteboardPermissionsStore.canIEdit && shouldCorrectUrlIfMismatch && hasUrlMismatch) {
      void updateDefaultFrameBackground()
    }
  }

  async function fetchDefaultFrameBackground(): Promise<void> {
    try {
      const response = await WhiteboardCardBackgroundsApi.fetchDefaultBackground(surfaceStore.wallHashid)
      initializeDefaultFrameBackground({
        backgroundType: response.backgroundType,
        backgroundUrl: response.backgroundUrl,
        shouldCorrectUrlIfMismatch: true,
      })
    } catch (error) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(error, { context: 'WhiteboardFrameBackgroundStoreFetchDefaultFrameBackground' })
    }
  }

  async function updateDefaultFrameBackground(payload?: {
    backgroundType: string
    backgroundUrl: string
  }): Promise<void> {
    try {
      const response = await WhiteboardCardBackgroundsApi.updateDefaultBackground({
        hashId: surfaceStore.wallHashid,
        backgroundType: payload?.backgroundType ?? previewDefaultFrameBackground.value.backgroundType,
        backgroundUrl: payload?.backgroundUrl ?? previewDefaultFrameBackground.value.backgroundUrl,
      })
      defaultFrameBackground.value = response
      previewDefaultFrameBackground.value = response
      isDefaultBackgroundConfigured.value = true
    } catch (error) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(error, { context: 'WhiteboardFrameBackgroundStoreUpdateDefaultFrameBackground' })
    }
  }

  async function fetchFrameBackgroundsAndDefaultFrameBackground(): Promise<void> {
    // Frame backgrounds must be fetched first since they contain the available backgrounds from Airtable
    // which are needed to validate the default background
    await fetchFrameBackgrounds()
    await fetchDefaultFrameBackground()
  }

  function setPreviewDefaultFrameBackground(payload: WhiteboardDefaultFrameBackground): void {
    previewDefaultFrameBackground.value = payload
  }

  function startPreviewBackgroundOnAllFrames(params?: { backgroundType: string; backgroundUrl: string }): void {
    const { backgroundType, backgroundUrl } = params ?? displayedDefaultFrameBackground.value
    isPreviewingFrameBackgrounds.value = true
    generateOriginalFrameBackgrounds()
    const previewDefaultCardData =
      backgroundType === BackgroundType.Custom
        ? generateCustomBackground(backgroundUrl)
        : backgroundTypeToBackground.value[backgroundType]
    previewFrameBackgrounds.value = Object.keys(originalFrameBackgrounds.value).reduce((acc, pageId) => {
      acc[pageId] = previewDefaultCardData
      return acc
    }, {})
    void whiteboardFramePreviewStore.updatePreviewForAllFrames()
  }

  function startPreviewBackgroundOnAllFramesUsingThemeFrameBackground(params?: {
    backgroundType: string
    backgroundUrl: string
  }): void {
    const editor = useTldrawEditor().editor
    if (editor == null) return
    const allPages = editor.getPages()

    const allPageIdsWithThemeFrameBackgroundMeta = allPages
      .filter(
        (page): page is TLPage => 'useThemeFrameBackground' in page.meta && page.meta.useThemeFrameBackground === true,
      )
      .map((page) => page.id)

    // Return early if no pages are using the theme card background
    if (allPageIdsWithThemeFrameBackgroundMeta.length === 0) return

    const { backgroundType, backgroundUrl } = params ?? displayedDefaultFrameBackground.value

    isPreviewingFrameBackgrounds.value = true
    generateOriginalFrameBackgrounds()
    const previewThemeFrameBackground =
      backgroundType === BackgroundType.Custom
        ? generateCustomBackground(backgroundUrl)
        : backgroundTypeToBackground.value[backgroundType]

    previewFrameBackgrounds.value = Object.keys(originalFrameBackgrounds.value).reduce<
      Record<TLPageId, WhiteboardCardBackground>
    >((acc, pageId) => {
      if (allPageIdsWithThemeFrameBackgroundMeta.includes(pageId as TLPageId)) {
        acc[pageId as TLPageId] = previewThemeFrameBackground
      }
      return acc
    }, {})

    allPageIdsWithThemeFrameBackgroundMeta.forEach((pageId) => {
      void whiteboardFramePreviewStore.updatePreviewForSingleFrame(pageId)
    })
  }

  function startPreviewBackgroundOnOtherFrames({
    currentFrameId,
    backgroundType,
    backgroundUrl,
  }: {
    currentFrameId: TLPageId
    backgroundType: string
    backgroundUrl: string
  }): void {
    generateOriginalFrameBackgrounds()
    const previewFrameBackground =
      backgroundType === BackgroundType.Custom
        ? generateCustomBackground(backgroundUrl)
        : backgroundTypeToBackground.value[backgroundType]
    previewFrameBackgrounds.value = Object.keys(originalFrameBackgrounds.value).reduce((acc, pageId) => {
      // Only preview the background on other frames
      if (pageId !== currentFrameId) {
        acc[pageId] = previewFrameBackground
      } else {
        // Keep the selected background on the current frame
        acc[pageId] = previewFrameBackgrounds.value[pageId]
      }
      return acc
    }, {})
    void whiteboardFramePreviewStore.updatePreviewForAllFrames()
  }

  function stopPreviewBackgroundOnOtherFrames({ currentFrameId }: { currentFrameId: TLPageId }): void {
    generateOriginalFrameBackgrounds()
    previewFrameBackgrounds.value = {
      // Only revert to original background on other frames
      ...originalFrameBackgrounds.value,
      [currentFrameId]: previewFrameBackgrounds.value[currentFrameId],
    }

    void whiteboardFramePreviewStore.updatePreviewForAllFrames()
  }

  function startPreviewDefaultFrameBackground(): void {
    isPreviewingDefaultFrameBackground.value = true
    previewDefaultFrameBackground.value = cloneDeep(defaultFrameBackground.value)
  }

  function stopPreviewDefaultFrameBackground(): void {
    isPreviewingDefaultFrameBackground.value = false
    previewDefaultFrameBackground.value = cloneDeep(defaultFrameBackground.value)
  }

  function stopPreviewDefaultAndAllFrameBackgrounds(): void {
    stopPreviewDefaultFrameBackground()
    stopPreviewFrameBackgrounds()
  }

  function backfillEmptyFrameBackgrounds(editor: Editor): void {
    const allPages = editor.getPages()
    allPages.forEach((page) => {
      // Pages created by tldraw don't have background type set
      // Backfill with Plain background to make the data consistent
      if (page.meta.backgroundType == null) {
        changeFrameBackground(page.id, BackgroundType.Plain, backgroundTypeToBackground.value[BackgroundType.Plain].url)
      }
    })
  }

  function migrateExistingFramesToUseThemeFrameBackground(): void {
    const editor = useTldrawEditor().editor
    if (editor == null) return
    const allPages = editor.getPages()
    const allPagesWithoutThemeFrameBackground = allPages.filter(
      (page) =>
        !('useThemeFrameBackground' in page.meta) &&
        page.meta.backgroundType === defaultFrameBackground.value.backgroundType,
    )
    allPagesWithoutThemeFrameBackground.forEach((page) => {
      setUseThemeFrameBackground(page.id, true)
    })

    // Fix pages with incorrect theme frame background metadata
    const allPagesWithWrongThemeFrameBackground = allPages.filter(
      (page) =>
        'useThemeFrameBackground' in page.meta &&
        page.meta.useThemeFrameBackground === true &&
        page.meta.backgroundType !== defaultFrameBackground.value.backgroundType,
    )
    allPagesWithWrongThemeFrameBackground.forEach((page) => {
      setUseThemeFrameBackground(page.id, false)
    })
  }

  function initializeFrameBackground(editor: Editor): void {
    migrateDeprecatedBackgrounds(editor)
    backfillEmptyFrameBackgrounds(editor)
    if (isAppUsing('sandboxThemeBackgrounds')) {
      migrateExistingFramesToUseThemeFrameBackground()
    }
    syncOriginalFrameBackgroundsWithFrameMeta()
  }

  function previewFrameBackground({
    shouldSetPreviewForAllExistingFrames = false,
    backgroundType,
    backgroundUrl,
  }: {
    shouldSetPreviewForAllExistingFrames?: boolean
    backgroundType: string
    backgroundUrl: string
  }): void {
    if (shouldSetPreviewForAllExistingFrames) {
      startPreviewBackgroundOnAllFrames({
        backgroundType,
        backgroundUrl,
      })
    } else {
      setPreviewFrameBackground({
        pageId: whiteboardStore.panelFrameId as TLPageId,
        payload:
          backgroundType === BackgroundType.Custom
            ? generateCustomBackground(backgroundUrl)
            : backgroundTypeToBackground.value[backgroundType],
      })
      void whiteboardFramePreviewStore.updatePreviewForSingleFrame(whiteboardStore.panelFrameId as TLPageId)
    }
  }

  // #endregion

  return {
    // Card backgrounds
    isPreviewingFrameBackgrounds,
    originalFrameBackgrounds,
    previewFrameBackgrounds,
    displayedFrameBackgrounds,
    isFrameBackgroundsPreviewDirty,
    backgroundTypeToBackground,
    frameBackgrounds,
    backgroundCategoryToBackgrounds,

    fetchFrameBackgrounds,
    getPageBackgroundUrl,
    startPreviewFrameBackgrounds,
    stopPreviewFrameBackgrounds,
    setPreviewFrameBackground,
    generateOriginalFrameBackgrounds,
    syncOriginalFrameBackgroundsWithFrameMeta,

    // Default card background
    defaultFrameBackground,
    previewDefaultFrameBackground,
    defaultFrameBackgroundName,
    isDefaultFrameBackgroundPreviewDirty,
    displayedDefaultFrameBackground,
    isDefaultBackgroundConfigured,
    fetchDefaultFrameBackground,
    updateDefaultFrameBackground,
    fetchFrameBackgroundsAndDefaultFrameBackground,
    setPreviewDefaultFrameBackground,
    startPreviewBackgroundOnAllFrames,
    startPreviewBackgroundOnOtherFrames,
    stopPreviewBackgroundOnOtherFrames,
    startPreviewDefaultFrameBackground,
    stopPreviewDefaultFrameBackground,
    stopPreviewDefaultAndAllFrameBackgrounds,
    initializeFrameBackground,
    initializeDefaultFrameBackground,
    previewFrameBackground,
    startPreviewBackgroundOnAllFramesUsingThemeFrameBackground,
  }
})
